import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Clock, Mail, ArrowLeft, Info, AlertCircle } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const HowJumpstartWorksSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext, streamType = 'generalist' }) => {
  const waysToMeet = [
    {
      title: 'You apply to roles',
      description: 'Submit applications before Sunday midnight every week. New roles go live throughout the week — apply early (just 2 clicks!).'
    },
    {
      title: 'Startups apply to you (Match Requests)',
      description: 'Startups may request to meet you directly at any time. Respond within 48 hours to accept or decline.'
    }
  ];

  const timeline = [
    {
      label: 'Monday – Friday',
      points: [
        'New roles go live from Monday to Friday. Apply early — startups can review applications as soon as you submit.',
        'You might receive Match Requests. You’re free to accept or decline — but must respond within 48 hours. If not, we’ll automatically reject it for you, and your account will be removed from our platform.'
      ]
    },
    {
      label: 'Sunday @ 23:59 (sharp!)',
      points: [
        'Application deadline. Once a role closes, it may not reopen.',
        'We’ve found making 7–10 applications throughout the week maximises chances.'
      ]
    },
    {
      label: 'Monday',
      points: [
        'All applications are now shared with startups.'
      ]
    },
    {
      label: 'Monday – Wednesday',
      points: [
        'Most startups respond within one week. If successful, you’ll be introduced by email to the hiring lead.',
        'Reply within 24 hours (REPLY ALL) to arrange the interview.'
      ]
    },
    {
      label: 'Tuesday onwards',
      points: [
        'Book an interview prep call with Jumpstart and prep for the interview.',
        'We’ll support you along the way — aim to land your next dream job!'
      ]
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">How Jumpstart Works</h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Meet startups in two ways - proactively apply to roles and receive Match Requests. Both happen in parallel.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          <StepCard title="📅 Two ways to meet startups">
            <div className="grid gap-6 md:grid-cols-2">
              {waysToMeet.map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#02B491] rounded-xl flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                      <p className="text-gray-700 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <InfoCard
              title="Tip"
              description="Stay active on the platform and responsive on email — momentum matters."
              type="tip"
            />
          </StepCard>

          <StepCard title="🗓 What your time in Jumpstart looks like">
            <div className="space-y-6">
              {timeline.map((block, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-5 h-5 text-gray-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg">{block.label}</h4>
                  </div>
                  <div className="space-y-3 ml-13">
                    {block.points.map((point, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-[#02B491] rounded-full mt-2"></div>
                        <p className="text-gray-700 leading-relaxed">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <InfoCard
              title="Important"
              description="Sunday 23:59 is the weekly deadline for applications. Late submissions won’t be considered."
              type="warning"
            />
          </StepCard>
        </div>

        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HowJumpstartWorksSection;


